import * as React from 'react';
import { animated, useTransition } from 'react-spring';
import BezierEasing from 'bezier-easing';
import styled from '@emotion/styled';
import { Switch, Route, useHistory } from 'react-router-dom';
import { Box, Flex, themeGet } from '@lightspeed/flame/Core';

import Localize from 'app/src/localize/Localize';
import { Eventbus } from 'app/src/data/Eventbus';
import GlobalEvents from 'app/GlobalEvents';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import { PaymentDefaultView, CardPaymentButton } from './PaymentDefaultView';
import { CancelCardView, CardView } from './CardView';
import { TotalPayment } from './components/TotalPayment';
import { PaymentStateProvider, usePaymentContext, PaymentViewEnum } from './Store/PaymentState';

export interface Theme {
  theme?: any;
}

const panelWidth = `300px`;
const panelShadowWidth = `20px`;
const easingFunction = BezierEasing(0, 0, 0.2, 1);
const animationDuration = 350;

const StyledWrapper = styled('div')`
  position: absolute;
  top: 54px;
  width: calc(100% - 240px);
  height: calc(100% - 54px);
  z-index: 10;
  overflow: hidden;
`;

const StyledOverlay = styled(animated.div)<Theme>`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const StyledPaymentsPanel = styled(animated.div)<Theme>`
  position: relative;
  background-color: ${themeGet('colors.white')};
  width: ${panelWidth};
  left: 100%;
  box-shadow: 0 0 ${panelShadowWidth} rgba(0, 0, 0, 0.4);
`;

export const StyledPaymentsPanelHeading = styled(Box)<Theme>`
  background-color: ${themeGet('colors.green-700')};
  color: ${themeGet('colors.white')};
  padding: ${themeGet('space.3')};
  text-transform: uppercase;
`;

interface PaymentsPanelComponentProps {}

const PaymentNavigationContainer: React.FC = () => {
  const { saleManager, paymentProvider } = usePaymentContext();
  const history = useHistory();

  const goToPaymentDefaultView = () => {
    history.push(PaymentViewEnum.PaymentDefaultView);
  };
  const onCreditDebitCancelled = () => {
    PaymentAnalytics.trackPaymentEvent('Leave', saleManager.sale.id, {
      paymentProcessor: paymentProvider.activeGatewayProcessorType,
    });
    goToPaymentDefaultView();
  };

  const goToCardView = () => {
    if (saleManager.pendingBalance.isZero()) {
      PaymentServiceAlert.alert('There is no balance left to pay.');
    } else {
      history.push(PaymentViewEnum.CreditCardView);
    }
  };
  return (
    <Box flexDirection="column" py={3} px={3}>
      <Switch>
        <Route exact path={PaymentViewEnum.PaymentDefaultView}>
          <CardPaymentButton goToCardView={goToCardView} />
          <PaymentDefaultView />
        </Route>
        <Route exact path={PaymentViewEnum.CreditCardView}>
          <CardView goToPaymentDefaultView={goToPaymentDefaultView} />
          <CancelCardView onCreditDebitCancelled={onCreditDebitCancelled} />
        </Route>
      </Switch>
    </Box>
  );
};

const PaymentsPanelComponent: React.FC<PaymentsPanelComponentProps> = () => {
  const [open, setOpen] = React.useState(false);
  const [remove, setRemove] = React.useState(!open);
  const showPanel = () => {
    setOpen(true);
    setRemove(false);
  };

  const hidePanel = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    Eventbus.listen({
      eventName: GlobalEvents.Events.register.SHOW_PAYMENTS,
      callbackFn: showPanel,
    });
    Eventbus.listen({
      eventName: GlobalEvents.Events.register.PAYMENTS_CLOSE,
      callbackFn: hidePanel,
    });

    return () => {
      Eventbus.unSubscribe(GlobalEvents.Events.register.SHOW_PAYMENTS, showPanel);
      Eventbus.unSubscribe(GlobalEvents.Events.register.PAYMENTS_CLOSE, hidePanel);
    };
  }, []);

  const overlayTransition = useTransition(open, null, {
    config: { duration: animationDuration, easing: easingFunction },
    from: { backgroundColor: 'rgba(0, 0, 0, 0)', boxShadow: 'inset 0 0px 0px rgba(0, 0, 0, 0.4)' },
    enter: {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      boxShadow: 'inset 0 5px 10px rgba(0, 0, 0, 0.4) ',
    },
    leave: {
      backgroundColor: 'rgba(0, 0, 0, 0)',
      boxShadow: 'inset 0 0px 0px rgba(0, 0, 0, 0.4)',
    },
    unique: true,
    onDestroyed: () => {
      !open && setRemove(true);
    },
  });

  const paymentPanelTransitions = useTransition(open, null, {
    config: { duration: animationDuration, easing: easingFunction },
    from: { transform: `translate3d(0px, 0, 0)` },
    enter: { transform: `translate3d(-${panelWidth}, 0, 0)` },
    leave: { transform: `translate3d(0px, 0, 0)` },
    unique: true,
  });

  return (
    <PaymentStateProvider>
      <StyledWrapper style={{ display: remove ? 'none' : 'block' }}>
        {overlayTransition.map(
          ({ item, key, props }) => item && <StyledOverlay key={key} style={props} />
        )}
        {paymentPanelTransitions.map(
          ({ item, key, props }) =>
            item && (
              <StyledPaymentsPanel key={key} style={props}>
                <Flex role="dialog" aria-label="paymentPanel" flexDirection="column">
                  <StyledPaymentsPanelHeading>
                    {Localize.translate('Payment')}
                  </StyledPaymentsPanelHeading>
                  <PaymentNavigationContainer />
                  <TotalPayment />
                </Flex>
              </StyledPaymentsPanel>
            )
        )}
      </StyledWrapper>
    </PaymentStateProvider>
  );
};
export { PaymentsPanelComponent };
